import { useQuery } from '@tanstack/react-query';
import { actions } from 'api/actions';
import { useSequentialTasks } from '@wix/gg-common-lib/hooks';
import { useEffect } from 'react';
import { useFeatureToggle } from './useFeatureToggle';
import { FeatureToggle } from 'config/experiments';
import { useProductStore } from 'stores/product';

type Props = {
  onError: (error: Error) => void;
};

export const useSiteSetup = ({ onError }: Props) => {
  const shouldInstallV3 = useFeatureToggle(FeatureToggle.InstallStoresAppV3);

  const { runTasks } = useSequentialTasks([
    ...(shouldInstallV3
      ? [
          { task: actions.createSitelessMetasite },
          { task: actions.installSiteApps },
          { task: actions.attachSiteToMetasite, retries: 4 },
        ]
      : [{ task: actions.createSiteFromTemplate }]),
    ...(useProductStore.getState().shouldAddSingleProductToStore
      ? [
          {
            task: async () => {
              await new Promise((resolve) => setTimeout(resolve, 5000));
            },
          },
          { task: actions.emptyStoreCatalog },
          { task: actions.addSingleProductToStore },
        ]
      : []),
    { task: actions.updateSiteSettings },
    { task: async () => actions.reportFlowSummary() },
  ]);

  const { isSuccess, isError, refetch, error } = useQuery({
    queryKey: ['site-setup'],
    queryFn: async () => {
      await runTasks();
      return {};
    },
  });

  useEffect(() => {
    if (error && isError) {
      onError(error);
    }
  }, [error, isError, onError]);

  return { isSuccess, isError, refetch };
};
